import React from 'react';
import { useNavigate } from 'react-router-dom';

export const TermsOfUsePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-white dark:bg-neutral-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/policies')}
            className="text-brand-teal hover:text-brand-teal-hover flex items-center mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Policies
          </button>
          <h1 className="text-4xl font-bold text-neutral-black dark:text-neutral-white mb-2">
            Terms of Use
          </h1>
          <p className="text-sm text-neutral-medium-gray dark:text-neutral-medium-gray">
            Last Updated: November 9, 2025
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <div className="space-y-8 text-neutral-black dark:text-neutral-white">

            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                By accessing or using ChatHTL ("Service"), you agree to be bound by these Terms of Use ("Terms").
                If you do not agree to these Terms, you may not access or use the Service. ChatHTL is operated by
                Horwath HTL ("we," "us," or "our").
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
              <p className="mb-4">
                ChatHTL is an AI-powered hospitality assistant that provides information, analysis, and insights
                related to hotel management, market analysis, and general hospitality industry questions. The Service
                uses artificial intelligence and may access various data sources to generate responses.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. User Accounts and Registration</h2>
              <p className="mb-4">
                <strong>3.1 Account Creation:</strong> To access certain features of the Service, you may be required
                to create an account. You agree to provide accurate, current, and complete information during registration.
              </p>
              <p className="mb-4">
                <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your
                account credentials and for all activities that occur under your account.
              </p>
              <p className="mb-4">
                <strong>3.3 Anonymous Use:</strong> Some features may be available for anonymous use with limited functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to any portion of the Service or related systems</li>
                <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
                <li>Use the Service to transmit any viruses, malware, or other harmful code</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
                <li>Collect or harvest any information from the Service using automated means</li>
                <li>Use the Service to generate, distribute, or store illegal or harmful content</li>
                <li>Attempt to reverse engineer, decompile, or discover the source code of the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. AI-Generated Content and Accuracy</h2>
              <p className="mb-4">
                <strong>5.1 No Guarantee of Accuracy:</strong> ChatHTL uses artificial intelligence to generate responses.
                While we strive for accuracy, we do not guarantee that all information provided is accurate, complete, or current.
              </p>
              <p className="mb-4">
                <strong>5.2 Verification Required:</strong> You should independently verify any important information obtained
                through the Service before relying on it for business decisions or other purposes.
              </p>
              <p className="mb-4">
                <strong>5.3 Not Professional Advice:</strong> Information provided by ChatHTL does not constitute professional
                advice (legal, financial, or otherwise) and should not be used as a substitute for consultation with professional advisors.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property Rights</h2>
              <p className="mb-4">
                <strong>6.1 Service Content:</strong> The Service and all content, features, and functionality are owned by
                Horwath HTL and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mb-4">
                <strong>6.2 User Content:</strong> You retain ownership of any content you submit to the Service. By submitting
                content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and process your content
                to provide and improve the Service.
              </p>
              <p className="mb-4">
                <strong>6.3 AI-Generated Content:</strong> Content generated by the AI in response to your queries is provided
                for your use, but we retain rights to use aggregated, anonymized data to improve the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Payment and Subscription Terms</h2>
              <p className="mb-4">
                <strong>7.1 Fees:</strong> Certain features of the Service may require payment of fees. All fees are in U.S.
                dollars unless otherwise stated.
              </p>
              <p className="mb-4">
                <strong>7.2 Billing:</strong> You authorize us to charge your selected payment method for all fees incurred.
                Payments are processed through Stripe.
              </p>
              <p className="mb-4">
                <strong>7.3 Subscriptions:</strong> Subscription fees are billed in advance on a recurring basis. Your subscription
                will automatically renew unless you cancel before the renewal date.
              </p>
              <p className="mb-4">
                <strong>7.4 Refunds:</strong> Fees are non-refundable except as required by law or as expressly stated in these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Privacy and Data Protection</h2>
              <p className="mb-4">
                Your use of the Service is subject to our Privacy Policy, which is incorporated into these Terms by reference.
                Please review our Privacy Policy to understand our data collection and processing practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Disclaimers and Limitation of Liability</h2>
              <p className="mb-4">
                <strong>9.1 AS-IS Basis:</strong> THE SERVICE IS PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS WITHOUT
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              </p>
              <p className="mb-4">
                <strong>9.2 Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, HORWATH HTL SHALL NOT
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS
                OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p className="mb-4">
                <strong>9.3 Maximum Liability:</strong> OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE
                SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100 USD,
                WHICHEVER IS GREATER.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Indemnification</h2>
              <p className="mb-4">
                You agree to indemnify, defend, and hold harmless Horwath HTL, its affiliates, and their respective officers,
                directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your
                use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Termination</h2>
              <p className="mb-4">
                <strong>11.1 By You:</strong> You may terminate your account at any time by contacting us at support@chathtl.com.
              </p>
              <p className="mb-4">
                <strong>11.2 By Us:</strong> We may suspend or terminate your access to the Service at any time, with or without
                cause, with or without notice, effective immediately.
              </p>
              <p className="mb-4">
                <strong>11.3 Effect of Termination:</strong> Upon termination, your right to use the Service will immediately
                cease. Provisions that by their nature should survive termination shall survive.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Governing Law and Dispute Resolution</h2>
              <p className="mb-4">
                <strong>12.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws
                of the State of Delaware, United States, without regard to its conflict of law provisions.
              </p>
              <p className="mb-4">
                <strong>12.2 Arbitration:</strong> Any dispute arising from these Terms or the Service shall be resolved through
                binding arbitration in accordance with the American Arbitration Association's rules, except that either party may
                seek injunctive relief in court.
              </p>
              <p className="mb-4">
                <strong>12.3 Class Action Waiver:</strong> You agree to resolve disputes with us on an individual basis and waive
                your right to participate in class actions or class-wide arbitration.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
              <p className="mb-4">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the
                updated Terms on this page and updating the "Last Updated" date. Your continued use of the Service after such
                changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Severability</h2>
              <p className="mb-4">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or
                eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
              <p className="mb-4">
                If you have questions about these Terms, please contact us at:
              </p>
              <p className="mb-4">
                Horwath HTL<br />
                Email: <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">support@chathtl.com</a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};
